import { Col, Container, Row } from 'react-bootstrap'
import { SelectOption } from '../../../../../../components/SelectDropdown/SelectDropdown'
import { Path, useFormContext } from 'react-hook-form'
import { IntegrationSettings } from '../../schema'
import { IntegrationServiceId, SALESFORCE_BASED_INTEGRATIONS } from '../../../../../../common/integrations'
import { useCallback, useMemo } from 'react'
import {
  DescriptorType,
  DetailedCRMAccountIntegrationMapping,
  FilterOperator,
  GroupOperator,
  type RelationshipSource,
  type RelationshipTarget,
} from '../../../../../../models/Account'
import { ObjectAndFieldSelectDropdown } from './ObjectAndFieldSelectDropdown'
import { FormFieldDefinition } from '../../hooks'

type Nullable<T> = { [K in keyof T]: T[K] | null }

type NullableContactLink = {
  source: Nullable<RelationshipSource>
  target: Nullable<RelationshipTarget>
}

export interface ContactLinkingRowsProps {
  serviceId: IntegrationServiceId
  mappingPath: string
  isDisabled?: boolean
  onChange?: () => void
}

export const ContactLinkingRows: React.FC<ContactLinkingRowsProps> = ({ serviceId, mappingPath, isDisabled = false, onChange }) => {
  const { watch, setValue } = useFormContext<IntegrationSettings>()

  const contactObjectRaw = watch(`mappings.contact.objectName` as const)

  const mapContactObject = useCallback(
    (objName: string | null) => {
      if (!SALESFORCE_BASED_INTEGRATIONS.includes(serviceId)) {
        return objName
      }
      if (objName === 'person_account') {
        return 'Account'
      }
      if (objName === 'account') {
        return 'Account'
      }
      if (objName === 'contact') {
        return 'Contact'
      }
      return objName
    },
    [serviceId],
  )

  const contactObject = useMemo(() => mapContactObject(contactObjectRaw ?? null), [contactObjectRaw, mapContactObject])

  const currentMapping = watch(mappingPath as Path<IntegrationSettings>) as DetailedCRMAccountIntegrationMapping | undefined

  const targetObjectName = currentMapping?.objectName || null

  const contactLinkingObjects: NullableContactLink[] = currentMapping?.contactLinks as NullableContactLink[]

  const relatedToLink = useMemo(() => {
    return (
      contactLinkingObjects?.find((link) => link.source?.object === contactObject || link.source?.object === 'AccountContactRelation') || {
        source: { object: contactObject ?? null, field: null },
        target: { object: targetObjectName, field: null },
      }
    )
  }, [contactLinkingObjects, contactObject, targetObjectName])

  const linkingObjectLink = useMemo(
    () => contactLinkingObjects?.find((link) => link.source?.object === targetObjectName) || null,
    [contactLinkingObjects, targetObjectName],
  )

  const updateContactLinks = useCallback(
    (updatedLinks: NullableContactLink[]) => {
      setValue(`${mappingPath}.contactLinks` as Path<IntegrationSettings>, updatedLinks, { shouldDirty: true, shouldValidate: true })
    },
    [mappingPath, setValue],
  )

  const onRelatedToContactLinkChanged = useCallback(
    (updatedLink: NullableContactLink) => {
      const updatedLinkWithFilters: NullableContactLink =
        updatedLink.source.object === 'AccountContactRelation' && updatedLink.source.field
          ? {
              ...updatedLink,
              source: {
                ...updatedLink.source,
                filters: [
                  {
                    groupOperator: GroupOperator.AND,
                    expressions: [
                      {
                        field: 'ContactId',
                        operator: FilterOperator.EQ,
                        source: {
                          type: DescriptorType.VARIABLE,
                          value: 'contactId',
                        },
                      },
                    ],
                  },
                ],
              },
            }
          : updatedLink

      const updatedAdditionalLinks =
        updatedLinkWithFilters.target.object !== targetObjectName
          ? linkingObjectLink
            ? {
                ...linkingObjectLink,
                target: {
                  object: updatedLinkWithFilters.target.object,
                  field: linkingObjectLink.target.field,
                  valueType: linkingObjectLink.target.valueType,
                  valueFormat: linkingObjectLink.target.valueFormat,
                },
              }
            : {
                source: {
                  object: targetObjectName ?? null,
                  field: updatedLinkWithFilters.target.field,
                  valueType: updatedLinkWithFilters.target.valueType,
                  valueFormat: updatedLinkWithFilters.target.valueFormat,
                },
                target: { object: updatedLinkWithFilters.target.object, field: null },
              }
          : null

      const updatedLinks =
        updatedLink.target.object !== targetObjectName
          ? [updatedLinkWithFilters, ...(updatedAdditionalLinks ? [updatedAdditionalLinks] : [])]
          : [updatedLinkWithFilters]

      updateContactLinks(updatedLinks)
      onChange?.()
    },
    [linkingObjectLink, targetObjectName, updateContactLinks, onChange],
  )

  const onLinkingObjectContactLinkChanged = useCallback(
    (updatedLink: NullableContactLink) => {
      const updatedLinks = [relatedToLink, updatedLink]
      updateContactLinks(updatedLinks)
      onChange?.()
    },
    [relatedToLink, updateContactLinks, onChange],
  )

  const predefinedRelatedToSourceObjectOptions: SelectOption[] = useMemo(() => {
    const options: SelectOption[] = []
    if (contactObject) {
      options.push({
        id: contactObject,
        label: contactObject,
      })
    }
    if (SALESFORCE_BASED_INTEGRATIONS.includes(serviceId)) {
      options.push({
        id: 'AccountContactRelation',
        label: 'AccountContactRelation',
      })
    }
    return options
  }, [contactObject, serviceId])

  const onSourceObjectSelected = (opt: SelectOption) => {
    const isSameSourceObject = relatedToLink.source.object === opt.id
    onRelatedToContactLinkChanged({
      source: {
        object: opt.id as string,
        field: isSameSourceObject ? relatedToLink.source.field : null,
        valueType: isSameSourceObject ? (relatedToLink.source.valueType ?? null) : null,
        valueFormat: isSameSourceObject ? (relatedToLink.source.valueFormat ?? null) : null,
      },
      target: relatedToLink.target,
    })
  }

  const onSourceFieldSelected = (opt: SelectOption, fieldDef: FormFieldDefinition | undefined) => {
    onRelatedToContactLinkChanged({
      source: {
        object: relatedToLink.source.object,
        field: opt.id as string,
        valueType: fieldDef?.type ?? null,
        valueFormat: fieldDef?.extraTypeInfo ?? null,
      },
      target: relatedToLink.target,
    })
  }

  const onTargetObjectSelected = (opt: SelectOption) => {
    const isSameTargetObject = relatedToLink.target.object === opt.id
    onRelatedToContactLinkChanged({
      source: relatedToLink.source,
      target: {
        object: opt.id as string,
        field: isSameTargetObject ? relatedToLink.target.field : null,
        valueType: isSameTargetObject ? relatedToLink.target.valueType : null,
        valueFormat: isSameTargetObject ? relatedToLink.target.valueFormat : null,
      },
    })
  }

  const onTargetFieldSelected = (opt: SelectOption, fieldDef: FormFieldDefinition | undefined) => {
    onRelatedToContactLinkChanged({
      source: relatedToLink.source,
      target: {
        object: relatedToLink.target.object,
        field: opt.id as string,
        valueType: fieldDef?.type ?? null,
        valueFormat: fieldDef?.extraTypeInfo ?? null,
      },
    })
  }

  const onAdditionalSourceObjectSelected = (opt: SelectOption, fieldDef: FormFieldDefinition | undefined) => {
    if (!linkingObjectLink) {
      return
    }
    onLinkingObjectContactLinkChanged({
      source: {
        object: linkingObjectLink.source.object,
        field: opt.id as string,
        valueType: fieldDef?.type ?? null,
        valueFormat: fieldDef?.extraTypeInfo ?? null,
      },
      target: linkingObjectLink.target,
    })
  }

  const onAdditionalTargetFieldSelected = (opt: SelectOption, fieldDef: FormFieldDefinition | undefined) => {
    if (!linkingObjectLink) {
      return
    }
    onLinkingObjectContactLinkChanged({
      source: linkingObjectLink.source,
      target: {
        object: linkingObjectLink.target.object,
        field: opt.id as string,
        valueType: fieldDef?.type ?? null,
        valueFormat: fieldDef?.extraTypeInfo ?? null,
      },
    })
  }

  return (
    <Container>
      <Row key='relatedToLinkRow' className='mb-3 align-items-center'>
        <Col className='d-flex align-items-center'>
          <ObjectAndFieldSelectDropdown
            serviceId={serviceId}
            objectName={relatedToLink?.source?.object || null}
            initialSelectedField={relatedToLink?.source?.field || null}
            predefinedObjectSelectOptions={predefinedRelatedToSourceObjectOptions}
            onObjectSelected={onSourceObjectSelected}
            onFieldSelected={onSourceFieldSelected}
            isFieldSelectionDisabled={isDisabled}
            isObjectSelectionDisabled={isDisabled}
          />
        </Col>
        <Col className='d-flex align-items-center'>
          <i className='bi bi-arrow-right mx-3 zocks-text-gray-500 fs-5'></i>
          <ObjectAndFieldSelectDropdown
            serviceId={serviceId}
            objectName={relatedToLink?.target?.object || null}
            initialSelectedField={relatedToLink?.target?.field || null}
            onObjectSelected={onTargetObjectSelected}
            onFieldSelected={onTargetFieldSelected}
            isFieldSelectionDisabled={isDisabled}
            isObjectSelectionDisabled={isDisabled}
          />
        </Col>
      </Row>
      {linkingObjectLink ? (
        <Row key='linkingObjectLinkRow' className='mb-3 align-items-center'>
          <Col className='d-flex align-items-center'>
            <ObjectAndFieldSelectDropdown
              serviceId={serviceId}
              objectName={linkingObjectLink?.source?.object || null}
              initialSelectedField={linkingObjectLink?.source?.field || null}
              onObjectSelected={() => {}}
              onFieldSelected={onAdditionalSourceObjectSelected}
              isObjectSelectionDisabled
              isFieldSelectionDisabled={isDisabled}
            />
          </Col>
          <Col className='d-flex align-items-center'>
            <i className='bi bi-arrow-right mx-3 zocks-text-gray-500 fs-5'></i>
            <ObjectAndFieldSelectDropdown
              serviceId={serviceId}
              objectName={linkingObjectLink?.target?.object || null}
              initialSelectedField={linkingObjectLink?.target?.field || null}
              onObjectSelected={() => {}}
              onFieldSelected={onAdditionalTargetFieldSelected}
              isObjectSelectionDisabled={isDisabled}
              isFieldSelectionDisabled={isDisabled}
            />
          </Col>
        </Row>
      ) : null}
    </Container>
  )
}
